import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import { useAccount, useConnect, useNetwork, useSwitchNetwork, useSignMessage } from "wagmi";
import { useAuth } from "../../context/AuthContext";
import { useEthersSigner } from "../../hooks/useEthersSigner";
import {
  ensureTreydoraWalletSession,
  formatWalletSessionError,
  WALLET_SIGN_IN_HINT,
} from "../../lib/ensureTreydoraWalletSession";
import {
  ETHEREUM_MAINNET_CHAIN_ID,
  getChainLabel,
  getSwitchToNetworkLabel,
} from "../../config/chainEnv";
import {
  createLibraryNftWalletIntent,
  cancelLibraryNftWalletIntent,
  confirmLibraryNftWalletPurchase,
} from "../../services/libraryApi";
import { clearPendingLibraryPurchase } from "../../utils/pendingLibraryPurchase";

const PROGRESS_LABELS = [
  "Reserving edition",
  "Waiting for payment",
  "Confirming payment",
  "Minting collectible",
  "Added to Collector Vault",
];

const PENDING_WALLET_INTENT_CODE = "PENDING_WALLET_INTENT";

function formatWeiEth(amountWei) {
  try {
    return `${ethers.utils.formatEther(amountWei)} ETH`;
  } catch {
    return "ETH";
  }
}

function formatWalletPurchaseError(raw) {
  const s = String(raw || "").trim();
  if (/NFT_COMIC_TREASURY_ADDRESS|Wallet purchase is not configured/i.test(s)) {
    return "ETH wallet purchase is not configured on the server (NFT_COMIC_TREASURY_ADDRESS). Card checkout remains available — use Buy with Card if you prefer.";
  }
  if (/Wallet price not configured|PRICE_WEI/i.test(s)) {
    return `Wallet ETH pricing is not configured on the server. ${s}`;
  }
  return s || "Wallet purchase failed.";
}

function resolveIntentChainId(intentPayload) {
  const n = Number(intentPayload?.chainId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function walletsMatch(a, b) {
  try {
    return ethers.utils.getAddress(String(a || "")) === ethers.utils.getAddress(String(b || ""));
  } catch {
    return false;
  }
}

function isPendingIntentConflict(err) {
  return (
    Number(err?.status) === 409 &&
    (err?.code === PENDING_WALLET_INTENT_CODE || /pending wallet purchase/i.test(String(err?.message || "")))
  );
}

/**
 * Treasury wallet purchase: backend intent supplies chainId + ETH quote; mint/sync after confirm.
 * Wallet-only sign-in via ensureTreydoraWalletSession — no email account required.
 */
export default function LibraryNftWalletPurchaseControls({
  editionId,
  quantity = 1,
  disabled = false,
  purchaseAllowed = true,
  /** When set, invoked instead of starting payment if `purchaseAllowed` is false (parent opens legal gate). */
  onLegalAcknowledgmentRequired,
  /** Parent registers a resume fn so legal Continue can restart wallet flow after ack. */
  registerWalletResume,
  onPurchaseComplete,
  compact = false,
}) {
  const navigate = useNavigate();
  const { walletLogin, linkWallet } = useAuth();
  const { address, isConnected } = useAccount();
  const { connectAsync, connectors } = useConnect();
  const { chain } = useNetwork();
  const { switchNetwork } = useSwitchNetwork();
  const { signMessageAsync } = useSignMessage();
  const signer = useEthersSigner();

  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(-1);
  const [hint, setHint] = useState("");
  const [err, setErr] = useState("");
  const [lastIntent, setLastIntent] = useState(null);
  const [blockedPendingIntent, setBlockedPendingIntent] = useState(null);

  const currentChainId = chain?.id != null ? Number(chain.id) : null;
  const activeIntent = blockedPendingIntent || lastIntent;
  const purchaseChainId = useMemo(() => {
    const fromIntent = resolveIntentChainId(activeIntent);
    return fromIntent ?? ETHEREUM_MAINNET_CHAIN_ID;
  }, [activeIntent]);

  const onWrongChain =
    Boolean(isConnected && address && currentChainId != null && purchaseChainId != null) &&
    currentChainId !== purchaseChainId;

  const ensureConnectedWallet = useCallback(async () => {
    let activeAddress = address;
    if (!isConnected || !activeAddress) {
      const connList = Array.isArray(connectors) ? connectors : [];
      const connector = connList.find((c) => c && c.ready) || connList[0];
      if (!connector) {
        throw new Error("No wallet connector available. Install MetaMask or enable WalletConnect.");
      }
      const connected = await connectAsync({ connector });
      activeAddress = connected?.account;
    }
    if (!activeAddress) {
      throw new Error("Wallet not connected.");
    }
    return activeAddress;
  }, [address, isConnected, connectors, connectAsync]);

  const ensureWalletSession = useCallback(
    async (activeAddress) => {
      setHint(WALLET_SIGN_IN_HINT);
      await ensureTreydoraWalletSession({
        walletAddress: activeAddress,
        signMessageAsync,
        walletLogin,
        linkWallet,
      });
      setHint("");
    },
    [signMessageAsync, walletLogin, linkWallet]
  );

  const sendPaymentForIntent = useCallback(
    async (intentPayload, activeAddress) => {
      const targetChainId = resolveIntentChainId(intentPayload);
      const treasury = String(intentPayload?.treasuryAddress || "").trim();
      const amountWei = String(intentPayload?.amountWei || "").trim();
      const intentId = String(intentPayload?.intentId || "").trim();
      const intentWallet = String(intentPayload?.walletAddress || "").trim();

      if (!targetChainId || !treasury || !amountWei || !intentId) {
        throw new Error("Invalid purchase intent from server.");
      }

      if (intentWallet && activeAddress && !walletsMatch(activeAddress, intentWallet)) {
        throw new Error(
          "Connect the same wallet you used when you started this purchase, or cancel and start over."
        );
      }

      setLastIntent(intentPayload);
      setBlockedPendingIntent(null);
      setStep(1);

      const liveChainId = chain?.id != null ? Number(chain.id) : null;
      if (liveChainId != null && liveChainId !== targetChainId) {
        if (switchNetwork) switchNetwork(targetChainId);
        setHint(
          `Switch to ${getChainLabel(targetChainId)} in your wallet, then press Continue Payment again.`
        );
        return;
      }

      setHint(
        `Send exactly ${formatWeiEth(amountWei)} on ${getChainLabel(targetChainId)} to complete purchase.`
      );

      if (!signer) {
        throw new Error("Wallet signer not ready. Open your wallet and try again.");
      }

      const tx = await signer.sendTransaction({
        to: treasury,
        value: ethers.BigNumber.from(amountWei),
      });
      setStep(2);
      setHint("Payment submitted — waiting for confirmation…");
      await tx.wait(1);

      setStep(3);
      const confirmPayload = await confirmLibraryNftWalletPurchase({
        intentId,
        txHash: tx.hash,
      });
      setStep(4);
      setLastIntent(null);
      setBlockedPendingIntent(null);
      setHint("On-chain collectible minted to your wallet. Opening Collector Vault…");
      if (typeof onPurchaseComplete === "function") {
        await onPurchaseComplete(confirmPayload);
      }
      setTimeout(() => navigate("/dashboard/library"), 1200);
    },
    [chain, switchNetwork, signer, onPurchaseComplete, navigate]
  );

  const runPurchase = useCallback(
    async ({ resumeIntent = null, forceNewIntent = false } = {}) => {
      setErr("");
      setHint("");
      setBusy(true);
      setStep(-1);
      clearPendingLibraryPurchase();
      try {
        if (!purchaseAllowed) {
          if (typeof onLegalAcknowledgmentRequired === "function") onLegalAcknowledgmentRequired();
          else setErr("Please review purchase terms before paying with ETH.");
          return;
        }

        const activeAddress = await ensureConnectedWallet();
        await ensureWalletSession(activeAddress);

        let intentPayload = resumeIntent;
        if (!intentPayload) {
          const qty = Math.max(1, Math.floor(Number(quantity) || 1));
          setStep(0);
          try {
            intentPayload = await createLibraryNftWalletIntent({
              editionId,
              quantity: qty,
              walletAddress: activeAddress,
            });
          } catch (createErr) {
            if (!forceNewIntent && isPendingIntentConflict(createErr) && createErr.pendingIntent) {
              setBlockedPendingIntent(createErr.pendingIntent);
              setLastIntent(createErr.pendingIntent);
              setHint(
                `You have an unfinished payment of ${formatWeiEth(createErr.pendingIntent.amountWei)}. Continue payment or cancel to start over.`
              );
              return;
            }
            throw createErr;
          }
        }

        await sendPaymentForIntent(intentPayload, activeAddress);
      } catch (e) {
        const raw = e?.reason || e?.message || String(e);
        if (
          String(raw).toLowerCase().includes("user rejected") ||
          String(raw).toLowerCase().includes("denied")
        ) {
          setErr("Transaction was cancelled in your wallet.");
        } else if (/rejected|denied|signature cancelled/i.test(String(raw))) {
          setErr(formatWalletSessionError(e));
        } else {
          setErr(formatWalletPurchaseError(raw));
        }
      } finally {
        setBusy(false);
      }
    },
    [
      purchaseAllowed,
      onLegalAcknowledgmentRequired,
      ensureConnectedWallet,
      ensureWalletSession,
      quantity,
      editionId,
      sendPaymentForIntent,
    ]
  );

  const continuePendingPayment = useCallback(async () => {
    if (!blockedPendingIntent) return;
    await runPurchase({ resumeIntent: blockedPendingIntent });
  }, [blockedPendingIntent, runPurchase]);

  const cancelPendingAndRestart = useCallback(async () => {
    const pending = blockedPendingIntent;
    if (!pending?.intentId) return;

    setErr("");
    setHint("");
    setBusy(true);
    clearPendingLibraryPurchase();
    try {
      await cancelLibraryNftWalletIntent({ intentId: pending.intentId });
      setBlockedPendingIntent(null);
      setLastIntent(null);
      setHint("Previous payment cancelled. Starting a fresh wallet purchase…");
      await runPurchase({ forceNewIntent: true });
    } catch (e) {
      const raw = e?.reason || e?.message || String(e);
      setErr(formatWalletPurchaseError(raw));
    } finally {
      setBusy(false);
    }
  }, [blockedPendingIntent, runPurchase]);

  const runPurchaseRef = useRef(runPurchase);
  runPurchaseRef.current = runPurchase;

  useEffect(() => {
    if (typeof registerWalletResume !== "function") return undefined;
    registerWalletResume(() => runPurchaseRef.current());
    return () => registerWalletResume(null);
  }, [registerWalletResume]);

  const noteChainLabel = getChainLabel(purchaseChainId);

  const buttonLabel = (() => {
    if (blockedPendingIntent) {
      if (busy) return "Processing…";
      if (onWrongChain) return getSwitchToNetworkLabel(purchaseChainId);
      return "Continue Payment";
    }
    if (!isConnected || !address) {
      if (busy) return "Connecting…";
      return "Connect Wallet & Pay with ETH";
    }
    if (onWrongChain) return getSwitchToNetworkLabel(purchaseChainId);
    if (busy && step >= 0) return PROGRESS_LABELS[Math.min(step, PROGRESS_LABELS.length - 1)];
    if (busy) return "Processing…";
    return "Pay with ETH";
  })();

  const ethPreview =
    activeIntent?.amountWei && !busy ? formatWeiEth(activeIntent.amountWei) : null;

  const handlePrimaryClick = () => {
    if (blockedPendingIntent) {
      void continuePendingPayment();
      return;
    }
    void runPurchase();
  };

  return (
    <div className={`lib-nft-wallet-pay${compact ? " lib-nft-wallet-pay--compact" : ""}`}>
      <p className="lib-nft-wallet-pay__note">
        No account required — your wallet signs you in. Wallet purchase sends ETH on {noteChainLabel} and
        mints/syncs the on-chain collectible after payment confirmation.
      </p>
      {ethPreview ? (
        <p className="lib-nft-wallet-pay__amount" aria-live="polite">
          {blockedPendingIntent ? "Pending payment:" : "Last quote:"}{" "}
          <strong>{ethPreview}</strong>
        </p>
      ) : null}
      {hint ? (
        <p className="lib-nft-wallet-pay__hint" role="status">
          {hint}
        </p>
      ) : null}
      {err ? (
        <p className="lib-nft-wallet-pay__err" role="alert">
          {err}
        </p>
      ) : null}
      <button
        type="button"
        className="lib-btn lib-btn--sm lib-nft-wallet-pay__btn"
        disabled={disabled || busy}
        aria-busy={busy ? "true" : undefined}
        onClick={handlePrimaryClick}
      >
        {buttonLabel}
      </button>
      {blockedPendingIntent ? (
        <button
          type="button"
          className="lib-btn lib-btn--sm lib-nft-wallet-pay__btn"
          disabled={disabled || busy}
          onClick={() => void cancelPendingAndRestart()}
        >
          Cancel and Start Over
        </button>
      ) : null}
    </div>
  );
}